import {Fragment, useEffect, useRef} from 'react';
import cx from 'classnames';

import {
  VIDEOS_BY_STEP,
  VIDEOS_BY_STEP_ALT,
  VIDEOS_BY_STEP_DESKTOP,
  VIDEOS_BY_STEP_DESKTOP_ALT,
} from '#component/page/ResultStory/ResultStory.util';
import {useViewportSize} from '#hook';

import {StoryVideosProps} from './StoryVideos.types';

import * as css from './StoryVideos.css';

/**
 * StoryVideos is a component used in the `ResultStory` route, and is
 * responsible for pre-loading and managing the playback state of all videos
 * used in the results story, according to the `activeStep` prop and the list
 * of videos provided by `ResultStory.util`.
 */
export const StoryVideos = ({
  autoAdvance = false,
  activeStep = 0,
  activeVideo = 0,
  className,
  onLoaded,
  onVideoEnded,
  useAltVideo = false,
  playbackRates = [],
  ...otherProps
}: StoryVideosProps) => {
  const {isDesktop} = useViewportSize();
  const containerEl = useRef<HTMLDivElement>(null);
  const desktopLoadedCount = useRef(0);
  const desktopVideos = useAltVideo ? VIDEOS_BY_STEP_DESKTOP_ALT : VIDEOS_BY_STEP_DESKTOP;
  const desktopVideoSrcs = desktopVideos.reduce(
    (currVideos, step) => [...currVideos, ...step],
    [] as string[],
  );
  const mobileLoadedCount = useRef(0);
  const mobileVideos = useAltVideo ? VIDEOS_BY_STEP_ALT : VIDEOS_BY_STEP;
  const mobileVideoSrcs = mobileVideos.reduce(
    (currVideos, step) => [...currVideos, ...step],
    [] as string[],
  );
  const renderedVideoSteps = isDesktop ? desktopVideos : mobileVideos;
  const renderedVideoSrcs = isDesktop ? desktopVideoSrcs : mobileVideoSrcs;
  const renderedVideosTotal = isDesktop ? desktopVideoSrcs.length : mobileVideoSrcs.length;
  const isLoaded = isDesktop
    ? desktopLoadedCount.current >= renderedVideosTotal
    : mobileLoadedCount.current >= renderedVideosTotal;

  const handlePlayVideo = (step: number, index: number) => {
    if (containerEl.current) {
      const videoEls = containerEl.current.querySelectorAll('video');

      videoEls.forEach(videoEl => {
        if (videoEl.dataset.step === `${step}` && videoEl.dataset.index === `${index}`) {
          // Avoid calling `.play()` if `autoPlay` attribute
          // has already caused video to start playing.
          const isPlaying =
            videoEl.currentTime > 0 &&
            !videoEl.paused &&
            !videoEl.ended &&
            videoEl.readyState > videoEl.HAVE_CURRENT_DATA;

          if (!isPlaying) {
            videoEl.playbackRate = playbackRates?.[index] || 1;
            // `play()` returns a Promise that rejects asynchronously (eg, with
            // an AbortError) if the video element is removed/paused before it
            // resolves. A synchronous try/catch cannot catch that rejection, so
            // we handle it on the Promise here to avoid logging in Sentry. This
            // is generally harmless for users.
            videoEl.play()?.catch(e => {
              console.warn('Playback interrupted', e);
            });
          }
        } else {
          videoEl.pause();
          // Defer resetting the video time until after the layout has updated
          // and the video is no longer visible. On resource-constrained devices
          // setting the currentTime immediately can result in a flash of
          // the first frame showing before the video is hidden.
          setTimeout(() => {
            videoEl.currentTime = 0;
          }, 100);
        }
      });
    }
  };

  useEffect(() => {
    handlePlayVideo(activeStep, activeVideo);
  }, [activeStep, activeVideo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const requests = [] as XMLHttpRequest[];

    const handleLoaded = () => {
      // `loadend` fires exactly once per request whether it succeeds, errors,
      // aborts, or times out. We count every settled request so that a failed
      // preload (eg, a network error on a single video asset) cannot leave the
      // progress permanently below 100% and strand the results story on the
      // loading screen. Any asset that failed to pre-cache will still be
      // streamed on demand by the native <video> element when it becomes active.
      if (isDesktop) {
        desktopLoadedCount.current = desktopLoadedCount.current + 1;
      } else {
        mobileLoadedCount.current = mobileLoadedCount.current + 1;
      }
      const loaded = isDesktop ? desktopLoadedCount.current : mobileLoadedCount.current;

      onLoaded?.({
        loaded,
        percent: Math.floor((loaded / renderedVideosTotal) * 100),
        total: renderedVideosTotal,
      });
    };

    if (!isLoaded) {
      renderedVideoSrcs.forEach(videoSrc => {
        const xhr = new XMLHttpRequest();

        xhr.open('GET', videoSrc);
        xhr.responseType = 'arraybuffer';
        xhr.send();
        requests.push(xhr);
      });

      requests.forEach(request => {
        request.addEventListener('loadend', handleLoaded);
      });
    }

    return () => {
      requests.forEach(request => {
        request.removeEventListener('loadend', handleLoaded);
      });
    };
    // We only want to re-run the hook if the viewport size changes (as defined
    // by "isDesktop"), and we haven't already pre-loaded the videos for that
    // viewport size. All other external variables used in this hook are derived
    // from these points of data, so they should be up-to-date if the hook is
    // fired when the below dependency changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  return (
    <div aria-hidden className={cx(className, css.root)} ref={containerEl} {...otherProps}>
      {renderedVideoSteps.map((videos, stepI) => {
        // Avoid mounting excess video elements if a step is
        // not active or immediately next/previous.
        const shouldRenderStep = [activeStep, activeStep + 1, activeStep - 1].includes(stepI);

        return shouldRenderStep ? (
          <Fragment key={stepI}>
            {videos.map((src, videoI) => {
              const isActiveVideo = activeVideo === videoI && stepI === activeStep;
              const shouldLoop = !autoAdvance && isActiveVideo && videoI === videos.length - 1;

              return (
                <video
                  autoPlay={isActiveVideo}
                  className={css.video({isActiveVideo})}
                  data-index={videoI}
                  data-step={stepI}
                  key={src}
                  loop={shouldLoop}
                  muted
                  onEnded={isActiveVideo ? onVideoEnded : undefined}
                  playsInline
                  preload="auto">
                  <source src={src} type="video/mp4" />
                </video>
              );
            })}
          </Fragment>
        ) : undefined;
      })}
    </div>
  );
};
